import React, { useEffect, useState } from 'react';
import { criarLancamento, listarLancamentos, receitaLiquida, removerLancamento, LancamentoFinanceiro } from '../services/financeiroService';
import { empresaService, Empresa } from '../services/empresaService';
import MainLayout from '../layouts/MainLayout';
import {
  Box, Container, Paper, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, TextField, MenuItem, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const tipos = [
  { value: 'entrada', label: 'Entrada' },
  { value: 'saida', label: 'Saída' },
];

type FormState = {
  id?: number;
  tipo: 'entrada' | 'saida';
  valor: string;
  descricao: string;
  data: string;
  observacao: string;
  empresaId: number | '';
};

export default function FinanceiroPage() {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [form, setForm] = useState<FormState>({ tipo: 'entrada', valor: '', descricao: '', data: '', observacao: '', empresaId: '' });
  const [lancamentos, setLancamentos] = useState<any[]>([]);
  const [filtros, setFiltros] = useState({ tipo: '', dataInicio: '', dataFim: '', empresaId: '' });
  const [receita, setReceita] = useState<number|null>(null);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editando, setEditando] = useState(false);

  const fetchLancamentos = async () => {
    setLoading(true);
    const data = await listarLancamentos(filtros);
    setLancamentos(data);
    setLoading(false);
  };

  const fetchReceita = async () => {
    if (filtros.dataInicio && filtros.dataFim) {
      const data = await receitaLiquida(filtros.dataInicio, filtros.dataFim);
      setReceita(data.receita);
    }
  };

  useEffect(() => {
    empresaService.listar().then(setEmpresas);
    fetchLancamentos();
    // eslint-disable-next-line
  }, [filtros]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | React.ChangeEvent<{ name?: string; value: unknown }>) {
    const { name, value } = e.target;
    setForm({ ...form, [name as string]: value });
  }

  function handleFiltroChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | React.ChangeEvent<{ name?: string; value: unknown }>) {
    const { name, value } = e.target;
    setFiltros({ ...filtros, [name as string]: value });
  }

  function handleOpenModal(novo: boolean, lancamento?: any) {
    if (novo) {
      setForm({ tipo: 'entrada', valor: '', descricao: '', data: '', observacao: '', empresaId: '' });
      setEditando(false);
    } else if (lancamento) {
      setForm({
        id: lancamento.id,
        tipo: lancamento.tipo,
        valor: String(lancamento.valor),
        descricao: lancamento.descricao,
        data: lancamento.data,
        observacao: lancamento.observacao || '',
        empresaId: lancamento.empresa?.id || ''
      });
      setEditando(true);
    }
    setOpenModal(true);
  }

  function handleCloseModal() {
    setOpenModal(false);
    setEditando(false);
    setForm({ tipo: 'entrada', valor: '', descricao: '', data: '', observacao: '', empresaId: '' });
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.empresaId) {
      alert('Selecione a empresa!');
      return;
    }
    await criarLancamento({ ...form, valor: parseFloat(form.valor), tipo: form.tipo as 'entrada' | 'saida', empresaId: Number(form.empresaId) });
    handleCloseModal();
    fetchLancamentos();
  };

  // Função para remover lançamento
  async function handleRemoverLancamento(id: number) {
    if (window.confirm('Tem certeza que deseja remover este lançamento?')) {
      // Chamar endpoint de remoção (ainda não existe, precisa criar no backend)
      await removerLancamento(id);
      fetchLancamentos();
    }
  }

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2">
                  Lançamentos Financeiros
                </Typography>
                <Button variant="contained" color="primary" onClick={() => handleOpenModal(true)}>
                  Nova entrada
                </Button>
              </Box>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  select
                  name="tipo"
                  label="Tipo"
                  value={filtros.tipo}
                  onChange={handleFiltroChange}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {tipos.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
                <TextField
                  select
                  name="empresaId"
                  label="Empresa"
                  value={filtros.empresaId}
                  onChange={handleFiltroChange}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.nome}</MenuItem>)}
                </TextField>
                <TextField
                  name="dataInicio"
                  label="Data início"
                  type="date"
                  value={filtros.dataInicio}
                  onChange={handleFiltroChange}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  name="dataFim"
                  label="Data fim"
                  type="date"
                  value={filtros.dataFim}
                  onChange={handleFiltroChange}
                  InputLabelProps={{ shrink: true }}
                />
                <Button onClick={fetchReceita} disabled={!filtros.dataInicio || !filtros.dataFim} variant="outlined">
                  Consultar Receita
                </Button>
                {receita !== null && (
                  <Typography sx={{ ml: 2 }}><b>Receita líquida:</b> R$ {receita.toFixed(2)}</Typography>
                )}
              </Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Lançamentos
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Valor</TableCell>
                        <TableCell>Descrição</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell>Empresa</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lancamentos.map((l: any) => (
                        <TableRow key={l.id}>
                          <TableCell>
                            <Chip
                              label={l.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                              color={l.tipo === 'entrada' ? 'success' : 'error'}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>R$ {Number(l.valor).toFixed(2)}</TableCell>
                          <TableCell>{l.descricao}</TableCell>
                          <TableCell>{l.data}</TableCell>
                          <TableCell>{l.empresa?.nome || '-'}</TableCell>
                          <TableCell align="center">
                            <IconButton color="primary" onClick={() => handleOpenModal(false, l)} size="small">
                              <EditIcon />
                            </IconButton>
                            <IconButton color="error" onClick={() => handleRemoverLancamento(l.id)} size="small">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar lançamento' : 'Novo lançamento'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            <TextField
              select
              name="tipo"
              label="Tipo"
              value={form.tipo}
              onChange={handleChange}
              required
              sx={{ minWidth: 120 }}
            >
              {tipos.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField
              select
              name="empresaId"
              label="Empresa"
              value={form.empresaId}
              onChange={handleChange}
              required
              fullWidth
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Selecione...</MenuItem>
              {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.nome}</MenuItem>)}
            </TextField>
            <TextField
              name="valor"
              label="Valor"
              type="number"
              inputProps={{ step: '0.01' }}
              value={form.valor}
              onChange={handleChange}
              required
              sx={{ minWidth: 100 }}
            />
            <TextField
              name="descricao"
              label="Descrição"
              value={form.descricao}
              onChange={handleChange}
              required
              sx={{ minWidth: 200 }}
            />
            <TextField
              name="data"
              label="Data"
              type="date"
              value={form.data}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              name="observacao"
              label="Observação (opcional)"
              value={form.observacao}
              onChange={handleChange}
              multiline
              minRows={2}
              sx={{ minWidth: 200 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">Salvar</Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
} 